/** @jsx jsx */
import React, { useEffect } from 'react';
import { css as compiledCss } from '@compiled/react';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import { Title } from '@helpcenter/styles/src/help-center';
import FallbackTopics from 'assets/images/fallback-topics.svg';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import topicsMessages from 'view/help-center-topics/topics/messages';
import { useIsPageInEditMode, useIsPageInPreviewMode } from 'view/layout-next/controllers';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import withAnalyticsContext from '@atlaskit/analytics-next/withAnalyticsContext';

import { css, jsx } from '@atlaskit/css';
import EmptyState from '@atlaskit/empty-state/empty-state';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { ExperienceSuccess } from '@atlassian/ufo/experience-success';
import type { listOfTopicsElementFragment$key } from './__generated__/listOfTopicsElementFragment.graphql';
import { helpCenterTopicsProductExperience } from './experiences';
import messages from './messages';
import Topic from './topic/topic';

const HC_ADS_DROP_COMMIT_2 = 'hc_ads_drop_commit_2';
const isHcAdsDropCommit2Enabled = (): boolean => FeatureGates.checkGate(HC_ADS_DROP_COMMIT_2);

const topicsSectionStyles = compiledCss({
	width: '264px',
	marginLeft: 'auto',
	marginRight: 'auto',
	borderRadius: token('radius.small', '4px'),
	backgroundColor: token('elevation.surface.raised', '#ffffff'),
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px rgba(9, 30, 66, 0.13), 0 0 1px 1px rgba(9, 30, 66, 0.08)',
	),
	zIndex: 1,
	'@media (min-width: 30em)': {
		width: '100%',
	},
	'@media (min-width: 60em)': {
		width: '100%',
		marginLeft: 0,
	},
});

const topicsSectionShapeThemeStyles = compiledCss({
	borderRadius: token('radius.large', '8px'),
});

const topicsSectionStyles1Column = compiledCss({
	width: '264px',
	'@media (min-width: 30em)': {
		width: '100%',
	},
	'@media (min-width: 60em)': {
		width: '336px',
		marginLeft: 0,
	},
});

const topicsListStyles = css({
	boxSizing: 'border-box',
	listStyleType: 'none',
	display: 'flex',
	flexFlow: 'row wrap',
	gap: token('space.400', '32px'),
	width: '100%',
	// Upgrading @atlaskit/platform; fix these violations.
	// eslint-disable-next-line @atlaskit/platform/expand-spacing-shorthand
	padding: token('space.400', '32px'),
});

const listItemStyles3Column = compiledCss({
	marginTop: 0,
	boxSizing: 'border-box',
	width: '100%',
	'@media (min-width: 60em)': {
		flexBasis: 'auto',
		width: 'calc((100% - 64px) / 3)',
	},
});

const listItemStyles2Column = compiledCss({
	marginTop: 0,
	boxSizing: 'border-box',
	width: '100%',
	'@media (min-width: 60em)': {
		flexBasis: 'auto',
		width: 'calc((100% - 64px) / 2)',
	},
});

const listItemStyles1Column = css({
	marginTop: 0,
	boxSizing: 'border-box',
	width: '100%',
	'@media (min-width: 60em)': {
		flexBasis: 'auto',
		width: '100%',
	},
});

const nonDraggableStyle = css({
	pointerEvents: 'none',
});

const FIX_TOPICS_MARKUP_A11Y_GATE = 'fix_topics_markup_a11y';
const isFixTopicsMarkupA11yEnabled = (): boolean =>
	FeatureGates.checkGate(FIX_TOPICS_MARKUP_A11Y_GATE);

interface ListOfTopicsProps {
	topicsElement: listOfTopicsElementFragment$key;
}

export const ListOfTopicsWithoutAnalyticsContext: React.FC<ListOfTopicsProps> = ({
	topicsElement,
}) => {
	di(useIsPageInEditMode);
	di(useIsPageInPreviewMode);
	di(isIncreasedFlexibilityEnabled);
	const data = useFragment<listOfTopicsElementFragment$key>(
		graphql`
			fragment listOfTopicsElementFragment on HelpLayoutTopicsListElement {
				topics {
					topicId
					hidden
					items {
						__typename
					}
					...topicCardFragment
				}
				visualConfig {
					titleColor
				}
			}
		`,
		topicsElement,
	);

	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isPageInEditMode] = useIsPageInEditMode();
	const [isPageInPreviewMode] = useIsPageInPreviewMode();

	const topics = data?.topics || [];

	const visibleTopics = topics.filter((topic) => !topic.hidden && topic.items?.length);

	useEffect(() => {
		if (!isPageInEditMode && visibleTopics.length > 0) {
			const event = createAnalyticsEvent({
				analyticsType: UI_EVENT_TYPE,
				action: 'viewed',
				topics_count: visibleTopics.length,
			});
			event.fire();
		}
	}, [createAnalyticsEvent, visibleTopics.length, isPageInEditMode]);

	if (visibleTopics.length === 0) {
		if (isPageInEditMode && !isPageInPreviewMode) {
			const message = isIncreasedFlexibilityEnabled()
				? messages.emptyStateTopicsDescription
				: messages.emptyStateDescription;
			return (
				<div css={nonDraggableStyle}>
					<EmptyState
						header=""
						maxImageWidth={250}
						description={intl.formatMessage(message, {
							b: (chunks: React.ReactNode) => <b>{chunks}</b>,
						})}
						imageUrl={FallbackTopics}
						width="wide"
					/>
				</div>
			);
		}
		return null;
	}

	const ListType = isFixTopicsMarkupA11yEnabled() ? 'ul' : 'ol';
	return (
		<ElementRoot elementWidth={ElementWidth.COMPACT}>
			<Title
				textColor={(isIncreasedFlexibilityEnabled() && data.visualConfig?.titleColor) || undefined}
			>
				{intl.formatMessage(topicsMessages.topicsSectionLabel)}
			</Title>
			<div
				data-testid="topics-section-container"
				css={[
					topicsSectionStyles,
					visibleTopics.length === 1 && topicsSectionStyles1Column,
					isHcAdsDropCommit2Enabled() && topicsSectionShapeThemeStyles,
				]}
			>
				<ListType css={topicsListStyles}>
					{visibleTopics.map((topic, index) => (
						<li
							key={topic.topicId}
							css={[
								visibleTopics.length > 2 && listItemStyles3Column,
								visibleTopics.length === 2 && listItemStyles2Column,
								visibleTopics.length === 1 && listItemStyles1Column,
							]}
						>
							<AnalyticsContext data={{ attributes: { topicPosition: index + 1 } }}>
								<Topic topic={topic} />
							</AnalyticsContext>
						</li>
					))}
				</ListType>
			</div>
			{!isPageInEditMode && <ExperienceSuccess experience={helpCenterTopicsProductExperience} />}
		</ElementRoot>
	);
};

export const ListOfTopics = withAnalyticsContext({
	component: 'topics',
})(ListOfTopicsWithoutAnalyticsContext);
